"use client";

// Settings → Team client panels (TEAM-UX-PRD §5–§6). Overview on-ramp/dashboard,
// Members + invites, Devices, and Primary & sync (assisted provisioning + the
// reused FleetSyncPanel). All data flows through contracts via useApi/apiCall —
// no raw fetch. Owner-gated affordances hide when the caller isn't the owner.

import { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  IntentBadge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatStrip,
  type StatStripItem,
  buttonVariants,
  formatRelativeTime,
} from "@excellent/ui";
import { Icon } from "@excellent/ui/icons";
import { useApi, apiCall, ApiError } from "@excellent/ui/api";
import { teamManage, type TeamMember, type TeamInvite } from "@excellent/ui/api/contracts/team-manage";
import { devices as devicesApi, type TeamDevice } from "@excellent/ui/api/contracts/devices";
import {
  provision as provisionApi,
  type ProvisionerInfo,
  type ProvisionTarget,
} from "@excellent/ui/api/contracts/provision";
import { sync as syncApi } from "@excellent/ui/api/contracts/sync";
import { auth as authApi } from "@excellent/ui/api/contracts/auth";
import { FleetSyncPanel } from "../database/fleet-sync-panel";

const INVITE_STATUS_INTENT: Record<TeamInvite["status"], "info" | "success" | "warning" | "neutral" | "danger"> = {
  pending: "info",
  consumed: "success",
  expired: "neutral",
  revoked: "danger",
};

function errMessage(e: unknown): string {
  if (e instanceof ApiError) return e.error;
  if (e instanceof Error) return e.message;
  return String(e);
}

async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* clipboard blocked — the value is still visible to select */
  }
}

// ===========================================================================
// Overview
// ===========================================================================

export function OverviewPanel() {
  const { data, isLoading, mutate } = useApi(teamManage.summary, {}, { refreshInterval: 8000 });

  // Flow D: consume a pending invite (stashed by `sync join`) once on first
  // authenticated Team load, then revalidate the summary so the new role/state
  // shows. A POST mutation, not the polled summary read (which is now pure).
  useEffect(() => {
    let cancelled = false;
    apiCall(teamManage.consumePending, {})
      .then((r) => {
        if (!cancelled && r.consumed) mutate();
      })
      .catch(() => {
        /* no pending invite is the steady state — non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, [mutate]);

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading…</p>;
  }
  const s = data;
  const isMultiplayer = s?.replica ?? false;

  if (!isMultiplayer) {
    return (
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>You’re working solo</CardTitle>
            <CardDescription>
              Excellent runs entirely on this machine. Add your team and everyone
              gets their own local copy — reads stay local-speed, your data stays
              yours, and each team runs its own small home-base server (no SaaS;
              we help you set it up). Two modes under Primary &amp; sync: serial
              multi-device (most-recent-edit-wins) works everywhere, and
              conflict-free team sync — where two people can edit the same record,
              online or offline, and nothing is lost — is available on
              Apple-Silicon Macs.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-3">
            <a href="/settings/workspace/team/primary" className={buttonVariants({})}>
              Set up your team
            </a>
            <span className="type-body text-muted-foreground">
              You’ll pick where the primary runs and we generate the keys for you.
            </span>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overviewStats: StatStripItem[] = [
    { title: "Members", value: String(s?.memberCount ?? 0), description: `${s?.ownerCount ?? 0} owner${(s?.ownerCount ?? 0) === 1 ? "" : "s"}` },
    { title: "Devices", value: String(s?.deviceCount ?? 0), description: "in this workspace" },
    { title: "Open invites", value: String(s?.openInviteCount ?? 0), description: "not yet accepted" },
    { title: "Primary", value: s?.primaryUrl ? "Connected" : "—", description: s?.primaryUrl ?? "no primary" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <StatStrip items={overviewStats} variant="standalone" />
      <Card>
        <CardHeader>
          <CardTitle>Manage your team</CardTitle>
          <CardDescription>Invite teammates, manage roles and devices, and watch live sync.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <a href="/settings/workspace/team/members" className={buttonVariants({ variant: "outline", size: "sm" })}>
            Members &amp; invites
          </a>
          <a href="/settings/workspace/team/devices" className={buttonVariants({ variant: "outline", size: "sm" })}>
            Devices
          </a>
          <a href="/settings/workspace/team/primary" className={buttonVariants({ variant: "outline", size: "sm" })}>
            Primary &amp; sync
          </a>
          <a href="/team" className={buttonVariants({ variant: "ghost", size: "sm" })}>
            Activity scoreboard
          </a>
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================================================================
// Members + invites
// ===========================================================================

export function MembersPanel() {
  const { data: summary } = useApi(teamManage.summary, {});
  const { data: membersData, mutate: refetchMembers, isLoading: membersLoading } = useApi(teamManage.members, {});
  const { data: invitesData, mutate: refetchInvites, isLoading: invitesLoading } = useApi(teamManage.invites, {});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const isOwner = summary?.callerIsOwner ?? false;
  const members = membersData?.members ?? [];
  const invites = invitesData?.invites ?? [];

  async function changeRole(m: TeamMember, role: "owner" | "member") {
    setError(null);
    setBusy(`role:${m.userId}`);
    try {
      const r = await apiCall(teamManage.setRole, { body: { userId: m.userId, role } });
      if (!r.ok) setError(r.reason === "last-owner" ? "A workspace must keep at least one owner." : r.reason ?? "Could not change role.");
      await refetchMembers();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(m: TeamMember) {
    if (!window.confirm(`Remove ${m.displayName} (@${m.handle})? Their sessions are revoked immediately.`)) return;
    setError(null);
    setBusy(`remove:${m.userId}`);
    try {
      const r = await apiCall(teamManage.removeMember, { body: { userId: m.userId } });
      if (!r.ok) setError(r.reason === "last-owner" ? "You can’t remove the last owner." : r.reason ?? "Could not remove member.");
      await refetchMembers();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function revokeInvite(inv: TeamInvite) {
    setError(null);
    setBusy(`invite:${inv.id}`);
    try {
      await apiCall(teamManage.revokeInvite, { body: { inviteId: inv.id } });
      await refetchInvites();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <DataTable<TeamMember>
            title="Members"
            subtitle="People with access to this workspace."
            headerAction={isOwner ? <InviteDialog onCreated={refetchInvites} /> : undefined}
            rowKey={(m) => m.userId}
            rows={members}
            empty={membersLoading ? "Loading…" : undefined}
            emptyState={
              membersLoading
                ? undefined
                : {
                    icon: <Icon name="users" size={28} />,
                    title: "No teammates yet",
                    description:
                      "You're working solo. Invite people to share this workspace — they'll appear here with their roles and devices.",
                    action: isOwner ? <InviteDialog onCreated={refetchInvites} /> : undefined,
                  }
            }
            columns={[
              {
                key: "person",
                header: "Person",
                render: (m) => (
                  <div className="flex flex-col">
                    <span className="text-foreground">{m.displayName}</span>
                    <span className="type-subtext text-muted-foreground">@{m.handle}</span>
                  </div>
                ),
              },
              {
                key: "role",
                header: "Role",
                render: (m) =>
                  isOwner ? (
                    <Select
                      value={m.role}
                      onValueChange={(v) => changeRole(m, v as "owner" | "member")}
                      disabled={busy === `role:${m.userId}`}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="capitalize">{m.role}</span>
                  ),
              },
              { key: "devices", header: "Devices", render: (m) => <span className="tabular-nums">{m.deviceCount}</span> },
              {
                key: "lastSeen",
                header: "Last active",
                render: (m) => <span className="text-muted-foreground">{m.lastSeenAt ? formatRelativeTime(m.lastSeenAt) : "never"}</span>,
              },
              ...(isOwner
                ? [
                    {
                      key: "actions",
                      header: "",
                      render: (m: TeamMember) => (
                        <Button variant="ghost" size="sm" disabled={busy === `remove:${m.userId}`} onClick={() => remove(m)}>
                          <Icon name="trash" /> Remove
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
          />

      <DataTable<TeamInvite>
            title="Invites"
            subtitle="Pending join links. Each is single-use and expires."
            rowKey={(i) => i.id}
            rows={invites}
            empty={invitesLoading ? "Loading…" : undefined}
            emptyState={
              invitesLoading
                ? undefined
                : {
                    icon: <Icon name="send" size={28} />,
                    title: "No pending invites",
                    description:
                      "Create a single-use join link to add a teammate. Links are one-time and expire automatically.",
                    action: isOwner ? <InviteDialog onCreated={refetchInvites} /> : undefined,
                  }
            }
            columns={[
              { key: "who", header: "For", render: (i) => i.handle ? `@${i.handle}` : <span className="text-muted-foreground">anyone with the link</span> },
              { key: "role", header: "Role", render: (i) => <span className="capitalize">{i.role}</span> },
              { key: "status", header: "Status", render: (i) => <IntentBadge intent={INVITE_STATUS_INTENT[i.status]}>{i.status}</IntentBadge> },
              { key: "expires", header: "Expires", render: (i) => <span className="text-muted-foreground">{formatRelativeTime(i.expiresAt)}</span> },
              ...(isOwner
                ? [
                    {
                      key: "actions",
                      header: "",
                      render: (i: TeamInvite) =>
                        i.status === "pending" ? (
                          <Button variant="ghost" size="sm" disabled={busy === `invite:${i.id}`} onClick={() => revokeInvite(i)}>
                            Revoke
                          </Button>
                        ) : null,
                    },
                  ]
                : []),
            ]}
          />
    </div>
  );
}

function InviteDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [handle, setHandle] = useState("");
  const [role, setRole] = useState<"owner" | "member">("member");
  const [ttlHours, setTtlHours] = useState("72");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ bundle: string; link: string; hasToken: boolean; secureLink: string | null } | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const r = await apiCall(teamManage.createInvite, {
        body: {
          handle: handle.trim() || undefined,
          role,
          ttlHours: Number(ttlHours) || 72,
        },
      });
      setResult({ bundle: r.joinBundle, link: r.joinLink, hasToken: r.hasToken, secureLink: r.secureLink });
      onCreated();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setOpen(false);
    setResult(null);
    setHandle("");
    setRole("member");
    setTtlHours("72");
    setError(null);
  }

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <Icon name="plus" /> Invite teammate
      </Button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : reset())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a teammate</DialogTitle>
            <DialogDescription>
              Creates a single-use, expiring join link. We never hand out your
              full-access primary token.
            </DialogDescription>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <FormField label="Handle (optional)" muted>
                <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="anita" />
              </FormField>
              <div className="flex gap-4">
                <FormField label="Role" muted>
                  <Select value={role} onValueChange={(v) => setRole(v as "owner" | "member")}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Expires in (hours)" muted>
                  <Input type="number" value={ttlHours} onChange={(e) => setTtlHours(e.target.value)} className="w-32" min={1} max={720} />
                </FormField>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={reset} disabled={busy}>Cancel</Button>
                <Button onClick={create} disabled={busy}>{busy ? "Creating…" : "Create invite"}</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              {result.secureLink ? (
                <>
                  <FormField label="Secure invite link — send this to your teammate" muted>
                    <div className="flex gap-2">
                      <Input readOnly value={result.secureLink} className="font-mono" />
                      <Button variant="outline" size="sm" onClick={() => copy(result.secureLink!)}>Copy</Button>
                    </div>
                  </FormField>
                  <p className="type-subtext text-muted-foreground">
                    The link carries no credentials — only a single-use code. Your
                    teammate opens it in their own Excellent (also at
                    localhost:3000); the connection details are fetched once,
                    encrypted, and verified against your team’s key.
                  </p>
                </>
              ) : (
                <Alert>
                  <AlertDescription>
                    This node doesn’t hold the fleet key, so a secure link can’t be
                    minted. Give your teammate the primary URL + a libSQL token out
                    of band, or run setup from the node that provisioned the
                    primary.
                  </AlertDescription>
                </Alert>
              )}
              {/* Terminal fallback ONLY when we cannot broker a secure link: the
                  join bundle now carries no token (the URL + single-use coupon),
                  so the owner supplies the libSQL token out of band — matching the
                  Alert above. When a secure link exists it IS the path; showing a
                  tokenless `--invite` command there would just mislead. */}
              {!result.secureLink && (
                <FormField label="Terminal alternative (paste on their machine)" muted>
                  <div className="flex gap-2">
                    <Input readOnly value={`excellent-mcp sync join --invite ${result.bundle}`} className="font-mono" />
                    <Button variant="outline" size="sm" onClick={() => copy(`excellent-mcp sync join --invite ${result.bundle}`)}>Copy</Button>
                  </div>
                </FormField>
              )}
              <DialogFooter>
                <Button onClick={reset}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ===========================================================================
// Devices
// ===========================================================================

export function DevicesPanel() {
  const { data: summary } = useApi(teamManage.summary, {});
  const { data, mutate, isLoading } = useApi(devicesApi.list, {});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [regenBusy, setRegenBusy] = useState(false);
  const isOwner = summary?.callerIsOwner ?? false;
  const rows = data?.devices ?? [];

  async function regenerateRecoveryCodes() {
    if (
      !window.confirm(
        "Regenerate recovery codes? Any previous unused codes stop working immediately.",
      )
    )
      return;
    setError(null);
    setRegenBusy(true);
    try {
      const r = await apiCall(authApi.regenerateRecoveryCodes, {});
      setCodes(r.recoveryCodes);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setRegenBusy(false);
    }
  }

  async function revoke(d: TeamDevice) {
    if (!window.confirm(`Revoke ${d.deviceName ?? d.deviceId}? Its active sessions are killed and it must sign in again.`)) return;
    setError(null);
    setBusy(d.deviceId);
    try {
      const r = await apiCall(devicesApi.revoke, { body: { deviceId: d.deviceId } });
      if (!r.ok) setError(r.reason ?? "Could not revoke device.");
      await mutate();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <DataTable<TeamDevice>
            title="Devices"
            subtitle="Machines signed into this workspace. Revoke a lost or retired device."
            rowKey={(d) => d.deviceId}
            rows={rows}
            empty={isLoading ? "Loading…" : undefined}
            emptyState={
              isLoading
                ? undefined
                : {
                    icon: <Icon name="monitor" size={28} />,
                    title: "No devices yet",
                    description:
                      "Machines that sign into this workspace show up here. Revoke a lost or retired one to kill its sessions.",
                  }
            }
            columns={[
              { key: "device", header: "Device", render: (d) => d.deviceName ?? <span className="font-mono type-subtext text-muted-foreground">{d.deviceId.slice(0, 12)}…</span> },
              { key: "owner", header: "Owner", render: (d) => (d.handle ? `@${d.handle}` : "—") },
              { key: "sessions", header: "Active sessions", render: (d) => <span className="tabular-nums">{d.activeSessions}</span> },
              { key: "lastSeen", header: "Last seen", render: (d) => <span className="text-muted-foreground">{d.lastSeenAt ? formatRelativeTime(d.lastSeenAt) : "never"}</span> },
              ...(isOwner
                ? [
                    {
                      key: "actions",
                      header: "",
                      render: (d: TeamDevice) =>
                        d.activeSessions > 0 ? (
                          <Button variant="ghost" size="sm" disabled={busy === d.deviceId} onClick={() => revoke(d)}>Revoke</Button>
                        ) : null,
                    },
                  ]
                : []),
            ]}
          />

      <Card>
        <CardHeader>
          <CardTitle>Recovery codes</CardTitle>
          <CardDescription>
            Passkeys live on your devices. If you lose them all, a recovery code is
            the only way back in. Generate a fresh set anytime — it invalidates any
            previous unused codes.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {codes ? (
            <>
              <Alert>
                <AlertDescription>
                  Save these now — they won’t be shown again, and each works once.
                </AlertDescription>
              </Alert>
              <ul className="grid grid-cols-2 gap-2 rounded-md border border-border/60 bg-muted/30 p-3">
                {codes.map((c) => (
                  <li key={c} className="font-mono type-subtext text-foreground">{c}</li>
                ))}
              </ul>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigator.clipboard?.writeText(codes.join("\n"))}>
                  Copy codes
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCodes(null)}>Done</Button>
              </div>
            </>
          ) : (
            <div>
              <Button variant="outline" size="sm" onClick={regenerateRecoveryCodes} disabled={regenBusy}>
                {regenBusy ? "Generating…" : "Regenerate recovery codes"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================================================================
// Primary & sync (assisted provisioning + reused FleetSyncPanel)
// ===========================================================================

export function PrimaryPanel() {
  const { data: summary } = useApi(teamManage.summary, {});
  const isReplica = summary?.replica ?? false;

  return (
    <div className="flex flex-col gap-6">
      <TeamSyncCard />
      {!isReplica && <ProvisionWizard />}
      <Card>
        <CardHeader>
          <CardTitle>Live sync</CardTitle>
          <CardDescription>Status, fleet roster, and a manual sync.</CardDescription>
        </CardHeader>
        <CardContent>
          <FleetSyncPanel />
        </CardContent>
      </Card>
      {isReplica && <RotateTokenCard />}
    </div>
  );
}

// WS3/WS4 — the in-app "turn on conflict-free team sync" switch. This is the
// real multiplayer plane (cr-sqlite CRR): two people can edit the same records,
// online or offline, and nothing is lost — genuine conflicts (money, stage,
// identity) surface for a human, the rest merge silently. It runs only where
// cr-sqlite is vendored (Apple-Silicon Macs today); the card refuses gracefully
// elsewhere rather than half-enabling a plane the OS can't run.
function TeamSyncCard() {
  const { data: caps, isLoading, mutate } = useApi(syncApi.teamCapabilities, {});
  const [relayUrl, setRelayUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [pairing, setPairing] = useState<{ deviceId: string; publicKey: string } | null>(null);

  useEffect(() => {
    if (caps?.relayUrl) setRelayUrl(caps.relayUrl);
  }, [caps?.relayUrl]);

  async function enable() {
    setBusy(true);
    setMsg(null);
    setPairing(null);
    try {
      const r = await apiCall(syncApi.enableTeam, { body: { relayUrl: relayUrl.trim() } });
      if (r.ok) {
        setMsg("Team sync is on. Restart Excellent to activate it, then share the device key below with your relay operator.");
        if (r.deviceId && r.publicKey) setPairing({ deviceId: r.deviceId, publicKey: r.publicKey });
      } else if (r.reason === "platform-unsupported") {
        setMsg("This machine can’t run conflict-free team sync (Apple-Silicon Macs only for now).");
      } else if (r.reason === "no-device") {
        setMsg("No device identity yet — start Excellent once, then try again.");
      } else {
        setMsg(r.reason ?? "Could not enable team sync.");
      }
      await mutate();
    } catch (e) {
      setMsg(errMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    setMsg(null);
    setPairing(null);
    try {
      const r = await apiCall(syncApi.disableTeam, {});
      setMsg(r.ok ? "Team sync turned off. Restart Excellent to return to single-player." : r.reason ?? "Could not turn off.");
      await mutate();
    } catch (e) {
      setMsg(errMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (isLoading || !caps) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Team sync (conflict-free)</CardTitle>
          <CardDescription>Checking whether this machine can run it…</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // WS4 — graceful refusal on an unsupported platform.
  if (!caps.available) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Team sync (conflict-free)
            <IntentBadge intent="neutral">Not available here</IntentBadge>
          </CardTitle>
          <CardDescription>{caps.reason}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Team sync (conflict-free)
          {caps.enabled && <IntentBadge intent="success">On</IntentBadge>}
        </CardTitle>
        <CardDescription>
          The real multiplayer plane. Two people can edit the same records — online
          or offline — and nothing is lost: money, pipeline stage, and identity
          conflicts surface for a human, everything else merges silently. Each team
          runs its own home-base relay; we help you set it up.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!caps.enabled ? (
          <>
            <FormField
              label="Relay URL"
              help="Your team’s self-hosted relay (see Provision, below, and the self-host runbook)."
            >
              <Input
                value={relayUrl}
                onChange={(e) => setRelayUrl(e.target.value)}
                placeholder="https://relay.your-team.example"
              />
            </FormField>
            <div>
              <Button onClick={enable} disabled={busy || !relayUrl.trim()}>
                {busy ? "Turning on…" : "Turn on team sync"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="type-body text-muted-foreground">
              Connected to <span className="font-mono text-foreground">{caps.relayUrl}</span>.
            </div>
            <div>
              <Button variant="outline" size="sm" onClick={disable} disabled={busy}>
                {busy ? "Turning off…" : "Turn off team sync"}
              </Button>
            </div>
          </>
        )}
        {msg && <p className="type-subtext text-muted-foreground">{msg}</p>}
        {pairing && (
          <Alert>
            <AlertDescription>
              <div className="type-subtext">
                Give this device key to your relay operator (devices allowlist):
                <pre className="mt-1 overflow-x-auto rounded bg-muted/40 p-2 font-mono type-subtext">
                  {`"${pairing.deviceId}": "${pairing.publicKey}"`}
                </pre>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function RotateTokenCard() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function rotate() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await apiCall(syncApi.rotateToken, {});
      setMsg(
        r.ok
          ? `Rotated. New token valid until ${r.expiresAt ?? "—"}. Restart Excellent to use it.`
          : r.reason === "no-fleet-key"
            ? "This node doesn’t hold the fleet key — rotate from the node that provisioned the primary."
            : r.reason ?? "Could not rotate.",
      );
    } catch (e) {
      setMsg(errMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Rotate this node’s token</CardTitle>
        <CardDescription>Mint a fresh libSQL token for this node and rewrite sync.env. Other nodes are unaffected.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div>
          <Button variant="outline" size="sm" onClick={rotate} disabled={busy}>{busy ? "Rotating…" : "Rotate token"}</Button>
        </div>
        {msg && <p className="type-subtext text-muted-foreground">{msg}</p>}
      </CardContent>
    </Card>
  );
}

function ProvisionWizard() {
  const { data: detect, isLoading: detecting } = useApi(provisionApi.detect, {});
  const [target, setTarget] = useState<ProvisionTarget | null>(null);
  const [recipe, setRecipe] = useState<{ commands: string[]; files: Record<string, string> } | null>(null);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualUrl, setManualUrl] = useState("");
  const [manualToken, setManualToken] = useState("");
  const [connectMsg, setConnectMsg] = useState<string | null>(null);
  // Optional public hostname → the recipe fronts sqld/relay with Caddy auto-HTTPS.
  const [domain, setDomain] = useState("");
  // Which home base to stand up: the sqld `primary` (solo/LWW, the default) or
  // the cr-sqlite team `relay` (real multiplayer, WS7). Relay launches on the VM
  // targets (docker/digitalocean/aws); `launchedService` remembers what the
  // in-flight/last provision was so the status section shows the right next step.
  const [service, setService] = useState<"primary" | "relay">("primary");
  const [launchedService, setLaunchedService] = useState<"primary" | "relay">("primary");
  const RELAY_TARGETS = new Set<ProvisionTarget>(["docker", "digitalocean", "aws"]);
  // Caddy-fronted (domain → auto-HTTPS) targets, shared by both services.
  const TLS_TARGETS = new Set<ProvisionTarget>(["docker", "digitalocean", "aws"]);

  // Poll provisioning status while something is in flight.
  const { data: status, mutate: refetchStatus } = useApi(provisionApi.status, {}, { refreshInterval: 4000 });
  const latest = status?.latest ?? null;
  const inFlight = latest?.status === "running";

  function selectTarget(t: ProvisionTarget) {
    // The relay preview is a fixed description (the recipe route previews the
    // primary), so only the primary flow fetches a per-target recipe.
    if (service === "relay") {
      setTarget(t);
      setRecipe(null);
      setError(null);
    } else {
      void loadRecipe(t);
    }
  }

  async function loadRecipe(t: ProvisionTarget) {
    setTarget(t);
    setRecipe(null);
    setError(null);
    try {
      const r = await apiCall(provisionApi.recipe, {
        query: { target: t, domain: domain.trim() || undefined },
      });
      setRecipe({ commands: r.commands, files: r.files });
    } catch (e) {
      setError(errMessage(e));
    }
  }

  async function launch() {
    if (!target) return;
    setLaunching(true);
    setError(null);
    setLaunchedService(service);
    try {
      const r = await apiCall(provisionApi.launch, {
        body: { target, service, domain: domain.trim() || undefined },
      });
      if (!r.ok && !r.async) setError(r.detail);
      await refetchStatus();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLaunching(false);
    }
  }

  async function connect() {
    setConnectMsg(null);
    try {
      const r = await apiCall(syncApi.connect, {
        body: { url: manualUrl.trim(), token: manualToken.trim() || undefined },
      });
      setConnectMsg(`Wrote ${r.syncEnvPath}. Restart Excellent to join:  ${r.sourceCommand} && pnpm dev`);
    } catch (e) {
      setConnectMsg(errMessage(e));
    }
  }

  const provisioners = detect?.provisioners ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Provision your sync home base</CardTitle>
        <CardDescription>
          Pick what to stand up and where. We generate the keys for you — no
          openssl, no hand-minted JWTs.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {error && (
          <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
        )}

        {/* Which home base: sqld primary (solo/LWW) vs cr-sqlite team relay */}
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              variant={service === "primary" ? "default" : "outline"}
              onClick={() => { setService("primary"); setTarget(null); setRecipe(null); }}
            >
              Primary (my devices)
            </Button>
            <Button
              variant={service === "relay" ? "default" : "outline"}
              onClick={() => { setService("relay"); setTarget(null); setRecipe(null); }}
            >
              Team relay (multiplayer)
            </Button>
          </div>
          <p className="type-subtext text-muted-foreground">
            {service === "primary"
              ? "A libSQL primary syncs your OWN devices (serial, last-write-wins)."
              : "The cr-sqlite relay is the real conflict-free team home base. It’s seeded with this device; add teammates to the roster afterward. Launches on Docker, DigitalOcean, or AWS."}
          </p>
        </div>

        {/* Detected targets */}
        <div className="grid gap-2 sm:grid-cols-2">
          {detecting && <p className="type-body text-muted-foreground">Checking for cloud CLIs…</p>}
          {provisioners
            .filter((p) => service === "primary" || RELAY_TARGETS.has(p.id))
            .map((p) => (
              <ProvisionerCard key={p.id} info={p} selected={target === p.id} onSelect={() => selectTarget(p.id)} />
            ))}
        </div>

        {/* Domain-for-HTTPS input, shared by both flows on Caddy-fronted targets. */}
        {/* (rendered inside each flow below) */}

        {/* Primary flow: recipe preview + launch */}
        {service === "primary" && target && recipe && (
          <div className="flex flex-col gap-3 border-t border-border/40 pt-4">
            <p className="type-body text-foreground">This will run:</p>
            <pre className="overflow-x-auto rounded-md bg-muted/40 p-3 type-subtext">{recipe.commands.join("\n")}</pre>
            {(provisioners.find((p) => p.id === target)?.supportsExecute) ? (
              <div className="space-y-3">
                {TLS_TARGETS.has(target) && (
                  <DomainInput domain={domain} setDomain={setDomain} onCommit={() => target && loadRecipe(target)} />
                )}
                <Button onClick={launch} disabled={launching || inFlight}>
                  {launching || inFlight ? "Launching…" : `Launch on ${shortTargetLabel(target)}`}
                </Button>
              </div>
            ) : (
              <Alert>
                <AlertDescription>
                  {target} is recipe-only here. Run the generated files on your
                  host, then use “I already have a primary” below.
                </AlertDescription>
              </Alert>
            )}
            {Object.entries(recipe.files).map(([name, contents]) => (
              <div key={name} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-mono type-subtext text-muted-foreground">{name}</span>
                  <Button variant="ghost" size="sm" onClick={() => copy(contents)}>Copy</Button>
                </div>
                <pre className="max-h-48 overflow-auto rounded-md bg-muted/40 p-3 type-subtext">{contents}</pre>
              </div>
            ))}
          </div>
        )}

        {/* Relay flow: fixed description + launch (no per-target recipe preview) */}
        {service === "relay" && target && (
          <div className="flex flex-col gap-3 border-t border-border/40 pt-4">
            <p className="type-body text-foreground">This will run:</p>
            <pre className="overflow-x-auto rounded-md bg-muted/40 p-3 type-subtext">
{`On ${shortTargetLabel(target)}, Excellent will:
 1. bring up a small Node container
 2. install the Excellent CLI and run: sync relay serve
 3. seed the device roster with THIS device`}
            </pre>
            {RELAY_TARGETS.has(target) ? (
              <div className="space-y-3">
                {TLS_TARGETS.has(target) && (
                  <DomainInput domain={domain} setDomain={setDomain} />
                )}
                <Button onClick={launch} disabled={launching || inFlight}>
                  {launching || inFlight ? "Launching…" : `Launch relay on ${shortTargetLabel(target)}`}
                </Button>
              </div>
            ) : (
              <Alert>
                <AlertDescription>The team relay launches on Docker, DigitalOcean, or AWS.</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Live provisioning status */}
        {latest && (
          <div className="border-t border-border/40 pt-4">
            <p className="type-body text-foreground">
              Provisioning status:{" "}
              <IntentBadge intent={latest.status === "reachable" ? "success" : latest.status === "failed" ? "danger" : "warning"}>
                {latest.status === "pending-dns" ? "waiting for DNS" : latest.status}
              </IntentBadge>
            </p>
            {latest.detail && <p className="mt-1 type-subtext text-muted-foreground">{latest.detail}</p>}
            {/* Offer the next step once the box is up: `reachable` (probed) OR
                `pending-dns` (TLS box up, cert pending). A primary writes the node
                config now (sync loop retries until any cert lands); a relay is
                wired via the Team-sync switch + `sync setup` on each device. */}
            {latest.host_url && (latest.status === "reachable" || latest.status === "pending-dns") && (
              launchedService === "relay"
                ? <RelayNextStep url={latest.host_url} />
                : <ConnectAfterProvision url={latest.host_url} />
            )}
          </div>
        )}

        {/* Manual connect */}
        <div className="flex flex-col gap-3 border-t border-border/40 pt-4">
          <p className="type-body text-foreground">I already have a primary</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input placeholder="http://primary-host:8080" value={manualUrl} onChange={(e) => setManualUrl(e.target.value)} />
            <Input placeholder="libSQL token (optional)" value={manualToken} onChange={(e) => setManualToken(e.target.value)} />
            <Button variant="outline" onClick={connect} disabled={!manualUrl.trim()}>Connect</Button>
          </div>
          {connectMsg && <p className="type-subtext text-muted-foreground">{connectMsg}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function ConnectAfterProvision({ url }: { url: string }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function connect() {
    setBusy(true);
    try {
      const r = await apiCall(syncApi.connect, { body: { url } });
      setMsg(`Wrote ${r.syncEnvPath}. Restart to become a replica:  ${r.sourceCommand} && pnpm dev`);
    } catch (e) {
      setMsg(errMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-3">
      <Button size="sm" onClick={connect} disabled={busy}>{busy ? "Writing config…" : "Convert this node to a replica"}</Button>
      {msg && <p className="mt-2 type-subtext text-muted-foreground">{msg}</p>}
    </div>
  );
}

/** Short, button-friendly provider label (the detect labels are verbose). */
function shortTargetLabel(t: ProvisionTarget): string {
  return { docker: "Docker", digitalocean: "DigitalOcean", flyio: "Fly.io", aws: "AWS", gcp: "GCP", manual: "Manual" }[t] ?? t;
}

/** The optional "domain → auto-HTTPS" input, shared by the primary + relay flows
 *  on Caddy-fronted targets. `onCommit` (primary) reloads the recipe on blur. */
function DomainInput({ domain, setDomain, onCommit }: { domain: string; setDomain: (v: string) => void; onCommit?: () => void }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="type-subtext text-muted-foreground">Domain for HTTPS (optional)</span>
      <Input
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        onBlur={onCommit}
        placeholder="sync.acme.com"
        className="max-w-xs"
      />
      <span className="type-subtext text-muted-foreground/70">
        A hostname turns on automatic HTTPS (Caddy + Let’s Encrypt) — point its DNS at the
        box. Leave blank for plain HTTP, safe only on a trusted LAN/VPN.
      </span>
    </label>
  );
}

/** Next step after a RELAY comes up: it's not a libSQL replica, so wire it via
 *  the Team-sync switch + `sync setup` on each teammate's machine. */
function RelayNextStep({ url }: { url: string }) {
  return (
    <div className="mt-3 space-y-2">
      <p className="type-subtext text-foreground">
        Relay is up at <span className="font-mono">{url}</span>. Turn on{" "}
        <strong>Team sync (conflict-free)</strong> above with this URL, then on each teammate’s
        machine run <span className="font-mono">excellent-mcp sync setup --mode team --relay {url}</span> and{" "}
        <span className="font-mono">sync pair</span> — adding each device to the roster and restarting the relay.
      </p>
      <Button size="sm" variant="outline" onClick={() => copy(url)}>Copy relay URL</Button>
    </div>
  );
}

function ProvisionerCard({ info, selected, onSelect }: { info: ProvisionerInfo; selected: boolean; onSelect: () => void }) {
  const ready = info.installed && (info.authenticated ?? true);
  return (
    <Card
      interactive
      asChild
      className={`gap-1 p-3 text-left ${selected ? "bg-selected ring-1 ring-primary" : ""}`}
    >
      <button type="button" onClick={onSelect}>
        <div className="flex items-center justify-between">
          <span className="type-body text-foreground">{info.label}</span>
          <IntentBadge intent={ready ? "success" : info.installed ? "warning" : "neutral"}>
            {info.installed ? (info.authenticated === false ? "needs login" : "ready") : "not found"}
          </IntentBadge>
        </div>
        <span className="type-subtext text-muted-foreground">{info.detail}</span>
        {!info.supportsExecute && <span className="type-subtext text-muted-foreground">Recipe-only</span>}
      </button>
    </Card>
  );
}
